import { z } from 'zod';
import { dateOnlySchema } from './common';
/**
 * GST returns: GSTR-1 and GSTR-3B.
 *
 * These shapes are the CA's deliverable, so they follow the government's own
 * table numbering rather than a naming of our own. A CA reading "table4A" knows
 * exactly which figure they are looking at and what it must reconcile against;
 * "b2bSupplies" would make them check.
 */
// ── Unit quantity codes ───────────────────────────────────────────────────
/**
 * The GST department's unit codes, for GSTR-1 Table 12.
 *
 * `document_lines.unit` is free text and holds things like "project", "month"
 * and "hour" - useful on a printed invoice, and not something the portal will
 * accept. A UQC comes from THIS list or the return is rejected.
 *
 * Abridged to the codes a services-and-light-goods business actually uses. The
 * full list runs to ~130 entries; adding one is a line here and nothing else,
 * whereas shipping all of them would put a 130-item dropdown in front of every
 * user to serve almost none of them.
 */
export const UQC_CODES = [
    { code: 'OTH', label: 'Others' },
    { code: 'NOS', label: 'Numbers' },
    { code: 'PCS', label: 'Pieces' },
    { code: 'UNT', label: 'Units' },
    { code: 'SET', label: 'Sets' },
    { code: 'BOX', label: 'Box' },
    { code: 'PAC', label: 'Packs' },
    { code: 'KGS', label: 'Kilograms' },
    { code: 'GMS', label: 'Grams' },
    { code: 'TON', label: 'Tonnes' },
    { code: 'LTR', label: 'Litres' },
    { code: 'MLT', label: 'Millilitres' },
    { code: 'MTR', label: 'Metres' },
    { code: 'CMS', label: 'Centimetres' },
    { code: 'SQF', label: 'Square feet' },
    { code: 'SQM', label: 'Square metres' },
    { code: 'HRS', label: 'Hours' },
    { code: 'DAY', label: 'Days' },
    { code: 'MON', label: 'Months' },
];
const UQC_SET = new Set(UQC_CODES.map((u) => u.code));
export const uqcSchema = z
    .string()
    .trim()
    .toUpperCase()
    .refine((v) => UQC_SET.has(v), { message: 'Not a recognised GST unit code' });
/**
 * A sensible UQC for a free-text unit, so existing data is not left blank.
 *
 * Best-effort only, and deliberately conservative: anything unrecognised
 * becomes OTH, which is valid on a return, rather than a guess that would put
 * a wrong code on a filing.
 */
export function inferUqc(unit) {
    if (!unit)
        return 'OTH';
    const normalised = unit.trim().toLowerCase();
    const direct = UQC_CODES.find((u) => u.code.toLowerCase() === normalised);
    if (direct)
        return direct.code;
    const guesses = {
        hour: 'HRS', hours: 'HRS', hr: 'HRS', hrs: 'HRS',
        day: 'DAY', days: 'DAY',
        month: 'MON', months: 'MON', monthly: 'MON',
        piece: 'PCS', pieces: 'PCS', pc: 'PCS',
        unit: 'UNT', units: 'UNT',
        number: 'NOS', numbers: 'NOS', no: 'NOS', nos: 'NOS', each: 'NOS',
        kg: 'KGS', kgs: 'KGS', kilogram: 'KGS', kilograms: 'KGS',
        gram: 'GMS', grams: 'GMS', g: 'GMS',
        litre: 'LTR', litres: 'LTR', liter: 'LTR', l: 'LTR',
        metre: 'MTR', metres: 'MTR', meter: 'MTR', m: 'MTR',
        set: 'SET', sets: 'SET',
        box: 'BOX', boxes: 'BOX',
        pack: 'PAC', packs: 'PAC',
        sqft: 'SQF', 'sq ft': 'SQF', 'square feet': 'SQF',
        sqm: 'SQM', 'square metres': 'SQM',
    };
    return guesses[normalised] ?? 'OTH';
}
// ── Period ────────────────────────────────────────────────────────────────
/**
 * A return period. GST is filed monthly or quarterly, always on calendar
 * months - the financial year matters for reporting, not for a return period.
 */
export const gstPeriodSchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
})
    .strict()
    .refine((v) => v.from <= v.to, { message: 'The period ends before it starts', path: ['to'] });
// ── The B2CL threshold ────────────────────────────────────────────────────
/**
 * Inter-state B2C invoices ABOVE this go in Table 5 (B2CL), invoice-wise;
 * everything else unregistered is summarised into Table 7 (B2CS).
 *
 * ── THE THRESHOLD IS A FUNCTION OF THE INVOICE DATE ──
 * Reduced from ₹2,50,000 to ₹1,00,000 by CGST Notification 12/2024, with
 * effect from 1 August 2024. So a return for July 2024 has to be computed at
 * the OLD threshold and August at the new one. A constant would silently
 * reclassify invoices every time an earlier period was re-run - the totals
 * would still tie, because both tables feed 3.1(a), and nothing would look
 * wrong.
 *
 * The comparison is strictly greater-than: an invoice of exactly ₹1,00,000
 * stays in the B2CS summary.
 */
export const B2CL_THRESHOLD_CHANGE_DATE = '2024-08-01';
export const B2CL_THRESHOLD_BEFORE = 250000;
export const B2CL_THRESHOLD_FROM_AUG_2024 = 100000;
export function b2clThresholdFor(invoiceDate) {
    return invoiceDate < B2CL_THRESHOLD_CHANGE_DATE ? B2CL_THRESHOLD_BEFORE : B2CL_THRESHOLD_FROM_AUG_2024;
}
export function isB2cl(facts) {
    if (facts.registered || !facts.interState)
        return false;
    return facts.total > b2clThresholdFor(facts.date);
}
export function classifyOutwardSupply(facts) {
    if (facts.registered)
        return 'b2b';
    return isB2cl(facts) ? 'b2cl' : 'b2cs';
}
/**
 * HSN digits required in Table 12, by aggregate annual turnover.
 *
 * Four digits up to ₹5 crore, six above it. Reporting fewer digits than
 * required is a defect the portal flags, so the review check needs the same
 * rule the return does.
 */
export const HSN_SIX_DIGIT_TURNOVER_THRESHOLD = 50000000;
export function hsnDigitsRequired(annualTurnover) {
    return annualTurnover > HSN_SIX_DIGIT_TURNOVER_THRESHOLD ? 6 : 4;
}
//# sourceMappingURL=gst.js.map